<template>
  <EBaseSnippet
    :title="title"
    :carousel-algorithm="carouselAlgorithm"
    :image-variant="imageVariant"
    :full-width-variant="fullWidthVariant"
    carousel-type="productAds"
    class="product-ads-carousel"
    :class="{
      'product-ads-carousel--image-variant': imageVariant,
      'product-ads-carousel--fullwidth-variant': fullWidthVariant,
    }"
    data-test-id="product-ads-carousel"
  >
    <template #heading>
      <ELink
        v-if="shoppingUrl"
        :href="shoppingUrl"
        color="result"
        data-track-id="product-ads-shopping"
      >
        {{ title }}
      </ELink>
      <template v-else>
        {{ title }}
      </template>
    </template>
    <template #header-end>
      <EAdLabel
        class="product-ads-carousel__ad-label"
        data-test-id="product-ads-carousel-ad-label"
        :tooltip-side="computedTooltipSide"
      >
        {{ $t('common.search.results.productads.label') }}
      </EAdLabel>
    </template>
    <ul
      data-test-id="product-ads-carousel-list"
      class="product-ads-carousel__list"
      :aria-label="title"
    >
      <li
        v-for="(item, index) in items"
        :key="index"
        class="product-ads-carousel__item snippet__item"
        data-test-id="product-ads-carousel-item"
      >
        <ESnippetCard
          ref="item"
          :url="item.url"
          :title="item.title"
          :aria-label="getAriaLabel(item)"
          :data-index="index"
          data-test-id="product-ads-carousel-card"
          compact
          @click="onItemClick(item)"
          @auxclick="onItemClick(item)"
        >
          <template #thumbnail>
            <ESnippetThumbnail
              class="product-ads-carousel__thumbnail"
              :src="adjustThumbnailUrl(item)"
              :image-alt="item.title"
              fit="contain"
              padded
              image-overlay
            />
          </template>
          <template #title-end>
            <div
              v-if="item.price"
              class="product-ads-carousel__price"
              data-test-id="product-ads-carousel-price"
            >
              {{ formatPrice(item.price) }}
            </div>
            <div class="product-ads-carousel__source" data-test-id="product-ads-carousel-source">
              <EIcon
                v-if="item.badge"
                icon="check-circle"
                size="s"
                class="product-ads-carousel__badge"
                data-track-id="pa-merchant-badge"
              />
              {{ item.source }}
            </div>
            <div v-if="item.shippingInfo" class="product-ads-carousel__info">
              {{ item.shippingInfo }}
            </div>
            <div v-if="item.pricePerUnit" class="product-ads-carousel__info">
              {{ item.pricePerUnit }}
            </div>
            <div v-if="item.energyRating" class="product-ads-carousel__info">
              {{ item.energyRating }}
            </div>
          </template>
        </ESnippetCard>
      </li>
      <li
        v-if="shoppingUrl && items.length >= shoppingTileDisplayThreshold"
        class="product-ads-carousel__item"
        data-test-id="product-ads-carousel-item"
      >
        <ECard
          :href="shoppingUrl"
          :elevation="0"
          as="a"
          class="product-ads-carousel__shopping-card"
          data-track-id="product-ads-shopping-tile"
        >
          <EIcon icon="shopping" size="m" />
          {{ $t('common.search.results.productads.more') }}
        </ECard>
      </li>
    </ul>
    <template v-if="shoppingUrl && !imageVariant" #footer>
      <EButton
        :href="shoppingUrl"
        as="a"
        variant="outline"
        size="s"
        icon="shopping"
        icon-size="m"
        data-track-id="product-ads-shopping-button"
      >
        {{ $t('common.search.results.productads.more') }}
      </EButton>
    </template>
  </EBaseSnippet>
</template>

<script>
import { mapState } from 'vuex';

import { batchCall } from '~~/common/js/universal/utils';
import { formatPrice } from '~~/common/js/universal/intl';
import { PageBasedScrolling } from '~~/common/js/client/carousel';
import screen from '~~/common/vue/mixins/screen';
import EAdLabel from '~~/common/vue/components/ad-label';
import EButton from '~~/common/vue/components/button';
import ECard from '~~/common/vue/components/card';
import ELink from '~~/common/vue/components/link';
import EIcon from '~~/common/vue/components/icon';
import { trackImpression, trackClick, TYPE_PRODUCT_AD } from '~~/search/common/js/client/analytics/results';
import { adjustThumbnailSize } from '~~/search/common/js/universal/urls';
import ESnippetCard from '~~/search/common/vue/components/snippets/card';
import EBaseSnippet from '~~/search/common/vue/components/snippets/base';
import ESnippetThumbnail from '~~/search/common/vue/components/snippets/thumbnail';

const THUMBNAIL_HEIGHT = 110;
const IMAGE_VARIANT_THUMBNAIL_HEIGHT = 130;
export const SHOPPING_TILE_DISPLAY_THRESHOLD = 4;

export default {
  name: 'EProductAdsCarousel',
  components: {
    EAdLabel,
    EButton,
    ECard,
    ELink,
    EIcon,
    EBaseSnippet,
    ESnippetCard,
    ESnippetThumbnail,
  },
  mixins: [screen()],
  props: {
    imageVariant: EBaseSnippet.props.imageVariant,
    fullWidthVariant: EBaseSnippet.props.fullWidthVariant,
    items: {
      type: Array,
      required: true,
    },
    firstHiddenItemIndex: {
      type: Number,
      default: null,
    },
    shoppingUrl: {
      type: String,
      default: '',
    },
    tooltipSide: EAdLabel.props.tooltipSide,
  },
  computed: {
    ...mapState('global', ['locale']),
    title() {
      return `${this.$t('common.search.results.productads.title')} ${this.$route.query.q?.toString()}`;
    },
    carouselAlgorithm() {
      return new PageBasedScrolling();
    },
    shoppingTileDisplayThreshold() {
      return SHOPPING_TILE_DISPLAY_THRESHOLD;
    },
    computedTooltipSide() {
      return (this.imageVariant || this.fullWidthVariant) ? 'right-top' : this.tooltipSide;
    },
  },
  mounted() {
    if (this.firstHiddenItemIndex) {
      this.initHiddenItemsObserver();
    }

    this.items.forEach((item) => {
      trackImpression(this.getTrackingOptions(item));
    });
  },
  methods: {
    getAriaLabel({ title, price }) {
      return `${title}: ${price ? this.formatPrice(price) : ''}`;
    },
    formatPrice({ value, currency }) {
      return formatPrice(this.locale, currency, value);
    },
    adjustThumbnailUrl(item) {
      const height = this.imageVariant && this.isDesktopUp ?
        IMAGE_VARIANT_THUMBNAIL_HEIGHT :
        THUMBNAIL_HEIGHT;
      return adjustThumbnailSize(item?.thumbnailUrl, { height });
    },
    initHiddenItemsObserver() {
      const batchCallback = batchCall((items) => this.$emit('hidden-items-visible', items.flat(2)), 500);
      const callback = (entries, obsvr) => {
        const visible = entries.filter(({ isIntersecting }) => isIntersecting);
        const items = visible.map(({ target }) => {
          const index = parseInt(target.dataset?.index, 10);
          const item = this.items[index];
          return item;
        });
        visible.forEach(({ target }) => {
          // @shopify/jest-dom-mocks has a bug where the 2nd argument
          // to this fallback is not actually the Observer, but a custom
          // internal data structure: https://github.com/Shopify/quilt/blob/master/packages/jest-dom-mocks/src/intersection-observer.ts#L31
          // this is a simple way to guard against it
          obsvr.unobserve?.(target);
        });
        batchCallback(items);
      };

      if (this.$refs.item) {
        const observer = new IntersectionObserver(callback, { root: this.$el });
        this.$refs.item
          .slice(this.firstHiddenItemIndex)
          .forEach(({ $el }) => observer.observe($el));
      }
    },
    onItemClick(item) {
      trackClick(this.getTrackingOptions(item));
      this.$emit('item-click', item);
    },
    getTrackingOptions({ title, rank, badge, source }) {
      return {
        extensions: badge ? [{ type: 'merchantBadge' }] : [],
        position: 'mainline',
        rank,
        title,
        type: TYPE_PRODUCT_AD,
        url: source,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.product-ads-carousel {
  position: relative;
}

.product-ads-carousel__list {
  display: flex;
  padding: 0;
  margin: 0;
  list-style: none;
}

.product-ads-carousel__item {
  display: inline-flex;
  flex-shrink: 0;
  padding: $space-1s $space-2s;
  scroll-snap-align: start;
  width: $snippet-item-width-mobile;

  @include desktop {
    // the width value (4.5) ensures an half card
    // is added to the end of the carousel.
    width: calc(100% / 4.5);
  }
}

.product-ads-carousel__price,
.product-ads-carousel__info,
.product-ads-carousel__source {
  @extend %ellipsis;
}

.product-ads-carousel__info,
.product-ads-carousel__source {
  font-size: $font-s;
}

.product-ads-carousel__thumbnail {
  background-color: $white;
}

.product-ads-carousel__price {
  font-size: $font-l;
  font-weight: $font-weight-1l;
  margin-top: $space-2s;
}

.product-ads-carousel__info {
  color: var(--color-text-secondary);
}

.product-ads-carousel__source {
  color: var(--color-url);
  margin-top: $space-2s;
}

.product-ads-carousel__badge {
  color: var(--color-decorative-icon);
}

.product-ads-carousel__ad-label {
  margin-right: 0;
  margin-left: auto;

  @include tablet {
    font-size: $font-m;
  }
}

.product-ads-carousel__shopping-card {
  align-items: center;
  display: flex;
  justify-content: center;
  flex-direction: column;
  padding: $space-s;
  text-align: center;
  gap: $space-1s;
  background-image: url(~~/search/common/vue/assets/img/product-ads-shopping.svg?asPath);
  background-repeat: no-repeat;
  background-position: bottom center;
  border: $border-width solid var(--color-decorative-border-1);

  &:hover,
  &:focus {
    text-decoration: underline;
  }
}

.product-ads-carousel--image-variant {
  .product-ads-carousel__list {
    margin-top: $space-s;
    margin-bottom: $space-s;
  }

  .product-ads-carousel__item {
    @include tablet {
      width: $product-ads-carousel-item-width-tablet;
    }

    @include desktop-1l {
      width: $product-ads-carousel-item-width-desktop-1l;
    }
  }
}

.product-ads-carousel--fullwidth-variant {
  .product-ads-carousel__item {
    @include desktop {
      width: $product-ads-carousel-item-width-fw-desktop;
    }

    @include desktop-1l {
      width: $product-ads-carousel-item-width-fw-desktop-1l;
    }
  }
}
</style>
